// Index arithmetic for the PDP picker's per-tile side maps (POR-13854).
//
// The picker keeps per-card customizations (brand logo, product images, active
// reference tab, popover placement) in objects keyed by the card's ARRAY INDEX.
// Splicing the rows array therefore has to remap those keys, or a card's settings
// end up attached to its neighbour. These helpers are pure so they can be verified
// without a browser; the widget owns the state and just re-assigns what it gets back.
//
// An `op` describes one splice and is exactly one of:
//   { insertAt, cloneFrom }  a row was inserted at `insertAt`, copied from `cloneFrom`
//   { removeAt }             the row at `removeAt` was deleted
//   { from, to }             the row at `from` was moved to `to`

// Maps an old row index to its new one. Returns null when the row no longer exists.
export function shiftIndex(op) {
  if (op && op.insertAt != null) {
    const at = op.insertAt;

    return (index) => (index >= at ? index + 1 : index);
  }

  if (op && op.removeAt != null) {
    const at = op.removeAt;

    return (index) => {
      if (index === at) {
        return null;
      }

      return index > at ? index - 1 : index;
    };
  }

  const from = op ? op.from : null;
  const to = op ? op.to : null;

  return (index) => {
    if (from == null || to == null || from === to) {
      return index;
    }

    if (index === from) {
      return to;
    }

    if (from < to) {
      return index > from && index <= to ? index - 1 : index;
    }

    return index >= to && index < from ? index + 1 : index;
  };
}

// Shallow-copy a map value so a duplicated card never shares the source's arrays.
// Product images are arrays of { url, name }; brand logos and tab keys are scalars.
function cloneMapValue(value) {
  if (Array.isArray(value)) {
    return value.map((item) => (item && typeof item === 'object' ? { ...item } : item));
  }

  if (value && typeof value === 'object') {
    return { ...value };
  }

  return value;
}

// Rebuilds an index-keyed map for the new row order. Returns a NEW object so Vue 2
// reactivity picks the change up without a $set per key.
export function remapIndexKeys(map, op) {
  const source = map || {};
  const shift = shiftIndex(op);
  const next = {};

  Object.keys(source).forEach((key) => {
    const index = Number(key);

    if (!Number.isInteger(index)) {
      return;
    }

    const moved = shift(index);

    if (moved === null) {
      return;
    }

    next[moved] = source[key];
  });

  if (op && op.insertAt != null && op.cloneFrom != null
    && Object.prototype.hasOwnProperty.call(source, op.cloneFrom)) {
    next[op.insertAt] = cloneMapValue(source[op.cloneFrom]);
  }

  return next;
}

// Same arithmetic for a single stored index (the "use for all images" owner row).
export function shiftScalarIndex(value, op) {
  if (value == null) {
    return null;
  }

  return shiftIndex(op)(value);
}

// "Elevated Hero (2)" -> "Elevated Hero". Only a trailing "(<digits>)" is stripped,
// so a real parenthetical like "Hero (Front)" survives.
export function baseLabel(label) {
  return String(label == null ? '' : label).replace(/\s*\(\d+\)\s*$/, '');
}

// The label for a copy: the base name plus the smallest free ordinal from 2 up. Counting
// same-type rows instead would collide once a mid-numbered duplicate is deleted — with
// "X" and "X (3)" left, a count of 2 would hand out "X (3)" a second time. (POR-13854)
export function nextDuplicateLabel(label, existingLabels) {
  const base = baseLabel(label);
  const taken = new Set((existingLabels || []).map((item) => String(item == null ? '' : item)));
  let ordinal = 2;

  while (taken.has(`${base} (${ordinal})`)) {
    ordinal += 1;
  }

  return `${base} (${ordinal})`;
}
